import 'client/common/polyfill';

import { useComponentRegistry } from '@bem-react/di';
import { IMetrikaOptions, YMInitializer } from 'client/common/components/yandex-metrika';
import React, { useRef } from 'react';
import Helmet from 'react-helmet';
import { Provider } from 'react-redux';
import { Route, Switch } from 'react-router-dom';

import Meta from 'client/common/components/meta';
import {
    ConfigContextProvider,
    SettingsContextProvider,
    UserContextProvider
} from 'client/common/contexts';
import { IAppData } from 'client/common/types';
import createStore, { IAppState } from 'store';

import 'client/common/components/app/index.css';
import 'client/common/styles/fonts.css';
import 'client/common/styles/meta.css';
import 'client/common/styles/variables.css';

export interface IAppProps {
    data: IAppData;
    state: IAppState;
}

function App({ data, state }: IAppProps) {
    const store = useRef(createStore(state));

    const {
        Footer,
        Prefooter,
        MainMenu,
        SidebarPusher,
        SidebarDimmer,
        Sidebar,
        SolutionPage,
        SolutionsPage
    } = useComponentRegistry('app');

    const renderSolutionPage = (props: IAppData) => () => {
        const { solutionsUrl, section, seo, solutionsData, shareServices, language } = props;
        const { solutionSection, solution } = solutionsData;
        const { tasks, products, industries, companySize } = solutionSection;

        return (
            <SolutionPage
                language={language}
                keywords={seo && seo.keywords}
                solution={solution}
                solutionsUrl={`${solutionsUrl}/${section}`}
                filter={{
                    tasks,
                    products,
                    industries,
                    companySize
                }}
                levels={data.levels}
                shareServices={shareServices.join(',')}
                />
        );
    };

    const renderSolutionsPage = (props: IAppData) => () => {
        const { solutionsUrl, solutionsData, filterUrl, section, language } = props;

        const { list, hasMore } = solutionsData;

        const {
            title,
            text,
            tasks,
            industries,
            products,
            companySize
        } = solutionsData.solutionsPage;

        return (
            <SolutionsPage
                language={language}
                section={section}
                title={title}
                text={text}
                solutionsUrl={solutionsUrl}
                filterUrl={filterUrl}
                solutionsList={list}
                hasMore={hasMore}
                filter={{ tasks, industries, products, companySize }}
                />
        );
    };

    const {
        metrikaCounters,
        metrikaCountersParams,
        settings,
        seo,
        og,
        siteSearchUrl,
        language,
        uatraits,
        faviconUrl,
        tld,
        regions,
        mainMenu,
        prefooter
    } = data;
    const { phone, text } = settings.footer;
    const phoneData = { phone, text };

    return (
        <div>
            <Provider store={store.current}>
                <SettingsContextProvider data={data}>
                    <ConfigContextProvider data={data}>
                        <UserContextProvider data={data}>
                            <Helmet>
                                <html lang={language} />
                                <link rel="shortcut icon" href={faviconUrl} />
                            </Helmet>
                            <Meta uatraits={uatraits} seo={seo} og={og} />

                            <MainMenu menu={mainMenu} siteSearchUrl={siteSearchUrl} />

                            <Sidebar
                                menu={mainMenu}
                                tld={tld}
                                regions={regions}
                                phoneData={phoneData}
                                siteSearchUrl={siteSearchUrl}
                                />
                            <SidebarDimmer />
                            <SidebarPusher>
                                <Switch>
                                    <Route
                                        exact
                                        path="/solutions"
                                        render={renderSolutionsPage(data)}
                                        />
                                    <Route
                                        exact
                                        path="/solutions/:section"
                                        render={renderSolutionsPage(data)}
                                        />
                                    <Route
                                        path="/solutions/cases/:slug"
                                        render={renderSolutionPage(data)}
                                        />
                                    <Route
                                        path="/solutions/analytics/:slug"
                                        render={renderSolutionPage(data)}
                                        />
                                    <Route
                                        path="/solutions/stories/:slug"
                                        render={renderSolutionPage(data)}
                                        />
                                </Switch>
                                <Prefooter sections={prefooter} />
                                <Footer />
                            </SidebarPusher>

                        </UserContextProvider>
                    </ConfigContextProvider>
                </SettingsContextProvider>
            </Provider>
            <YMInitializer
                accounts={metrikaCounters.map(counter => Number(counter.id))}
                options={prepareMetikaOptions()}
                />
            <script src={data.shareScript} />
        </div>
    );

    function prepareMetikaOptions() {
        return metrikaCounters.reduce((acc: IMetrikaOptions, counter) => {
            acc[counter.id] = { ...metrikaCountersParams, ...counter.params };

            return acc;
        }, {});
    }
}

export default App;
